import * as React from "react";

import "./headshot.scss";

import {cc}               from "@Common/react/callback-chain";
import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Load2}            from "@Common/react/load2";
import {scopedState}      from "@Common/react/scoped-state";
import {ConditionalValue} from "@Common/react/shadow/conditional-value";
import {scn, scnv}        from "@Common/react/state-change-nested-value";
import {UseState}         from "@Common/react/use-state";
import {cnv}              from "@Common/utils/arr-path";
import {unique}           from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {equalDeep, omit}  from "@Common/utils/objects";

import {AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

import {HeadshotDisplay} from "./headshot-display";
import {HeadshotGrid}    from "./headshot-grid/headshot-grid";
import {HeadshotUpload}  from "./headshot-upload";

export const Headshot = ({initEnrollment, program, agentId, next}) => cs(
    ["disabled", (_, next) => next(!program.programPreferences.useImage)],
    ({disabled}) => ConditionalValue({
        _key: disabled,
        // disabled: false,
        renderShadow: (next) => (
            disabled ? next({
                enrollmentEditor: {
                    $name: "Headshot disabled",
                    prepareSave: (enrollment) => ({
                        newValue: cnv(enrollment, ["profile"], (_) => (
                            omit(_, ["defaultHeadshot", "defaultHeadshot2"])
                        )),
                    }),
                    valid: true,
                },
                disabled: true,
            }) : (
                HeadshotEnabled({initEnrollment, program, agentId, next})
            )
        ),
        next,
    }),
);


// When not disabled
const HeadshotEnabled = ({initEnrollment, program, agentId, next}) => cs(
    consumeContext("apis"),
    ["headshots", ({apis}, next) => Load2({
        fetch: async () => apis.next.gallery.headshots.getImages({agentId}),
        next,
    })],
    ["selectedHeadshots", (_, next) => UseState({
        getInitValue: () => chain(
            !initEnrollment ? null : [
                initEnrollment.profile?.defaultHeadshot,
                initEnrollment.profile?.defaultHeadshot2,
            ].filter(v=>v),
            (_) => ({
                current: _,
                saved  : _,
            })
        ),
        next,
    })],
    ["calValue", ({selectedHeadshots, headshots}, next) => next(cc(null, [
        (_, next) => (
            headshots.loading ? (
                null
            ) : next()
        ),
        (_, next) => (
            selectedHeadshots.value.current == null ? (
                next()
            ) : chain(
                selectedHeadshots.value.current,
                (_) => _.filter((printUrl) => headshots.value.some((hs) => hs.printUrl === printUrl)),
                unique,
            )
        ),
        (_, next) => chain(
            [
                headshots.value?.find((hs) => hs.defaultSlot === 1)?.printUrl,
                headshots.value?.find((hs) => hs.defaultSlot === 2)?.printUrl,
            ]
                .filter(v=>v),
            (_) => _.length ? _ : next(),
        ),
        () => headshots.value.slice(0,1).map((_) => _.printUrl),
    ]))],
    ["grid", ({headshots, selectedHeadshots, calValue}, next) => HeadshotGrid({
        headshots: headshots.value,
        program,
        getSelectedNum: (url) => chain(
            calValue.indexOf(url),
            (i) => i===-1 ? null : i + 1,
        ),
        setSelecteds: scopedState(selectedHeadshots, ["current"]).onChange,
        next,
    })],
    ({headshots, selectedHeadshots, calValue, grid}) => next({
        enrollmentEditor: {
            $name: "Headshot",
            loading: headshots.loading,
            prepareSave: (enrollment) => ({
                newValue: cnv(enrollment, ["profile"], (_) => ({
                    ...omit(_, ["defaultHeadshot", "defaultHeadshot2"]),
                    ...calValue?.[0] && {
                        defaultHeadshot : calValue?.[0],
                    },
                    ...calValue?.[1] && {
                        defaultHeadshot2: calValue?.[1],
                    },
                })),
                onSaved: () => scn(selectedHeadshots, ["saved"], () => calValue),
            }),
            changed: !equalDeep(selectedHeadshots.value.saved, calValue),
            // valid: !!calValue?.length,
            valid: !grid.invalid,
            errorsHandling: grid.errorsHandling,
        },
        getValue: () => calValue,
        render: ({imageName, showErrors}) => cs(
            ["headshotDisplay", (_, next) => HeadshotDisplay({
                headshots, selectedHeadshots, imageName, agentId,
                calValue,
                next,
            })],
            ["headshotUpload", (_, next) => HeadshotUpload({
                headshots, selectedHeadshots, imageName, agentId,
                next,
            })],
            consumeContext("company"),
            consumeContext("userLevels"),

            ["userCanEdit", ({company, userLevels}, next) => next(
                userLevels.userLevel === COMPANY_LEVEL || (
                    userLevels.userLevel === BRANCH_LEVEL && company?.prefs?.officeCanEditHeadshot1
                ) || (
                    userLevels.userLevel === AGENT_LEVEL && company?.prefs?.agentCanEditHeadshot
                )
            )],

            ({headshotUpload, headshotDisplay, userCanEdit}) => (
                <div {...{
                    className: "headshot-6ef",
                    "xd-qa-type": "card",
                    "xd-qa-name": "Headshot",
                }}>
                    <div className="header">
                        <div className="left">
                            <div {...{
                                className: "title",

                                "xd-qa-type": "label",
                                "xd-qa-name": "Headshot",
                            }}>
                                {imageName}
                            </div>

                            {grid.renderControls()}
                        </div>

                        <div className="right">
                            <a {...{
                                onClick: () => {
                                    scnv(selectedHeadshots, ["current"], () => null);
                                },

                                "xd-qa-type": "button",
                                "xd-qa-name": "Revert to Default",
                            }}>
                                Revert to Default
                            </a>

                            {!!headshots.value?.length && !!userCanEdit && (
                                <button {...{
                                    onClick: () => headshotUpload.show(),
                                    "xd-qa-type": "button",
                                    "xd-qa-name": "Add Headshot",
                                }}>ADD {imageName.toUpperCase()}</button>
                            )}
                        </div>
                    </div>

                    {!userCanEdit && (
                        <div {...{
                            className: "not-allowed",

                            "xd-qa-type": "text",
                            "xd-qa-name": "Error message - Not allowed",
                        }}>
                            You do not have the ability to modify headshots. Please see your admin for more information.
                        </div>
                    )}

                    {grid.render({
                        imageName,
                        showErrors,
                        ...!!userCanEdit && {
                            onEdit: (hs) => headshotDisplay.editModal.show({
                                data: {
                                    ...hs,
                                    defaultSlot: calValue?.includes(hs.printUrl) ? 1 : 0,
                                },
                                type: "Headshot",
                                typeLabel: imageName,
                            }),
                            onAdd: () => headshotUpload.show(),
                            onDelete: headshotDisplay.deleteHeadshot,
                        },
                    })}

                </div>
            ),
        ),
    }),
);


